// Configuration des seuils de version pour les différents composants
// Format: { component: string, minVersion: string, recommendedVersion: string, criticalStatus: boolean }

// Valeurs par défaut - peuvent être surchargées par les valeurs sauvegardées
export const defaultVersionThresholds = {
  // Bases de données
  mariadb: {
    minVersion: '10.9.8',
    recommendedVersion: '10.11.4',
    critical: true,
    description: 'Versions antérieures à 10.9.8 contiennent des vulnérabilités de sécurité critiques'
  },
  
  // Applications
  apache: {
    minVersion: '2.4.55',
    recommendedVersion: '2.4.58',
    critical: false,
    description: 'Versions antérieures à 2.4.55 ne sont plus maintenues avec les correctifs de sécurité'
  },
  nodejs: {
    minVersion: '16.18.0',
    recommendedVersion: '18.16.0',
    critical: true,
    description: 'Versions antérieures à 16.x ne bénéficient plus du support de sécurité'
  },
  
  // Applications métier
  gitPatientAppVersion: {
    minVersion: 'v2.14.9',
    recommendedVersion: 'v7.35.28',
    critical: false,
    description: 'Versions antérieures à v2.14.9 ont des limitations fonctionnelles importantes'
  },
  gitManagerFrontAppVersion: {
    minVersion: 'v2.8.3',
    recommendedVersion: 'v3.1.0',
    critical: false,
    description: 'Versions antérieures à v2.8.3 ne supportent pas les nouvelles fonctionnalités'
  },
  gitMarshallerVersion: {
    minVersion: 'v1.7.2',
    recommendedVersion: 'v1.9.0',
    critical: true,
    description: 'Versions antérieures à v1.7.2 ont des problèmes de sécurité avec les interopérabilités'
  },
  gitMarshallerInteropVersion: {
    minVersion: 'v0.5.9',
    recommendedVersion: 'v0.7.0',
    critical: false,
    description: 'Versions antérieures à v0.5.9 ne supportent pas les formats d\'échange récents'
  }
};

// Variable pour stocker les seuils actuels (par défaut ou personnalisés)
let currentThresholds = { ...defaultVersionThresholds };

// Fonctions utilitaires pour comparer les versions
export const compareVersions = (v1, v2) => {
  // Retire le "v" au début des versions si présent
  const version1 = v1.startsWith('v') ? v1.substring(1) : v1;
  const version2 = v2.startsWith('v') ? v2.substring(1) : v2;
  
  // Divise les versions en composants (par exemple "10.11.2" devient [10, 11, 2])
  const parts1 = version1.split('.').map(part => parseInt(part, 10) || 0);
  const parts2 = version2.split('.').map(part => parseInt(part, 10) || 0);
  
  // Compare chaque partie de la version
  for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
    const part1 = parts1[i] || 0;
    const part2 = parts2[i] || 0;
    
    if (part1 > part2) return 1;
    if (part1 < part2) return -1;
  }
  
  return 0; // Versions égales
};

// Vérifie si une version est obsolète
export const isObsolete = (component, version) => {
  if (!version || !currentThresholds[component]) return false;
  
  // Si la version est vide ou "N/A", elle n'est pas obsolète
  if (version === '' || version === 'N/A') return false;
  
  const threshold = currentThresholds[component];
  return compareVersions(version, threshold.minVersion) < 0;
};

// Vérifie si une version est à mettre à jour (non critique mais pas la dernière recommandée)
export const needsUpdate = (component, version) => {
  if (!version || !currentThresholds[component]) return false;
  
  // Si la version est vide ou "N/A", elle n'a pas besoin de mise à jour
  if (version === '' || version === 'N/A') return false;
  
  const threshold = currentThresholds[component];
  const compareToMin = compareVersions(version, threshold.minVersion);
  const compareToRecommended = compareVersions(version, threshold.recommendedVersion);
  
  // Si au-dessus du minimum mais en-dessous de la recommandée
  return compareToMin >= 0 && compareToRecommended < 0;
};

// Obtient le niveau d'alerte pour une version
export const getAlertLevel = (component, version) => {
  if (isObsolete(component, version)) {
    return currentThresholds[component].critical ? 'critical' : 'warning';
  }
  
  if (needsUpdate(component, version)) {
    return 'info';
  }
  
  return 'ok';
};

// Obtient un message de statut pour une version
export const getVersionStatus = (component, version) => {
  if (!version || !currentThresholds[component]) {
    return { status: 'unknown', message: 'Composant non reconnu' };
  }
  
  // Si la version est vide ou "N/A"
  if (version === '' || version === 'N/A') {
    return { status: 'none', message: 'Non installé' };
  }
  
  const threshold = currentThresholds[component];
  
  if (isObsolete(component, version)) {
    return {
      status: 'obsolete',
      alertLevel: threshold.critical ? 'critical' : 'warning',
      message: threshold.description,
      recommendation: `Mettre à jour vers la version ${threshold.minVersion} ou supérieure`
    };
  }
  
  if (needsUpdate(component, version)) {
    return {
      status: 'outdated',
      alertLevel: 'info',
      message: `Version fonctionnelle mais non optimale`,
      recommendation: `Mise à jour vers ${threshold.recommendedVersion} recommandée`
    };
  }
  
  return {
    status: 'current',
    alertLevel: 'ok',
    message: 'Version à jour'
  };
};

// Fonction pour obtenir tous les serveurs avec des problèmes de version
export const getServersWithVersionIssues = (clients, includeOutdated = false) => {
  const serversWithIssues = [];
  
  clients.forEach(client => {
    client.servers.forEach((server, serverIndex) => {
      const componentIssues = [];
      
      // Vérifier chaque composant
      Object.keys(currentThresholds).forEach(component => {
        // Si le composant existe sur ce serveur
        if (server[component]) {
          // Vérifier si c'est obsolète
          if (isObsolete(component, server[component])) {
            componentIssues.push({
              component,
              version: server[component],
              status: 'obsolete',
              alertLevel: currentThresholds[component].critical ? 'critical' : 'warning',
              message: currentThresholds[component].description,
              recommendation: `Mettre à jour vers la version ${currentThresholds[component].minVersion} ou supérieure`
            });
          } 
          // Ou si c'est juste à mettre à jour (non critique)
          else if (includeOutdated && needsUpdate(component, server[component])) {
            componentIssues.push({
              component,
              version: server[component],
              status: 'outdated',
              alertLevel: 'info',
              message: `Version fonctionnelle mais non optimale`,
              recommendation: `Mise à jour vers ${currentThresholds[component].recommendedVersion} recommandée`
            });
          }
        }
      });
      
      // S'il y a des problèmes, ajouter ce serveur à la liste
      if (componentIssues.length > 0) {
        serversWithIssues.push({
          clientId: client.id,
          clientName: client.name,
          serverIndex,
          serverName: server.name,
          serverType: server.type || 'Serveur tout-en-un',
          serverColor: server.color,
          componentIssues,
          hasCritical: componentIssues.some(issue => issue.alertLevel === 'critical')
        });
      }
    });
  });
  
  // Trier avec les problèmes critiques en premier
  return serversWithIssues.sort((a, b) => {
    if (a.hasCritical && !b.hasCritical) return -1;
    if (!a.hasCritical && b.hasCritical) return 1;
    return 0;
  });
};

// Fonction pour éditer les seuils de version
export const updateVersionThreshold = (component, field, value) => {
  if (currentThresholds[component]) {
    currentThresholds[component][field] = value;
    return true;
  }
  return false;
};

// Fonction pour charger des seuils personnalisés
export const loadCustomThresholds = (customThresholds) => {
  // Fusionner les seuils personnalisés avec les valeurs par défaut
  currentThresholds = { ...defaultVersionThresholds };
  
  Object.keys(customThresholds).forEach(component => {
    if (currentThresholds[component]) {
      currentThresholds[component] = {
        ...currentThresholds[component],
        ...customThresholds[component]
      };
    }
  });
  
  return currentThresholds;
};

// Fonction pour obtenir les seuils actuels
export const getCurrentThresholds = () => {
  return { ...currentThresholds };
};
